/**
 * Where a door swings, as keep-outs for floor props.
 *
 * The swing is the collision code's (`calculateDoorZone`,
 * `collision/doorCollision.js`): the leaf's quarter circle on the side it
 * actually opens to — measured off the leaf, so it is right from either room
 * — plus the threshold strip you walk through, always on this room's side.
 * A door that swings OUT of the room keeps only that strip here; a door whose
 * hinge is unknown keeps both arcs. One rule for "in a door swing" across the
 * bathroom's floor props, the bedroom's rug, tree and brought nightstand, and
 * the vanity and cabinet checks that already used it.
 *
 * A door the collision code cannot seat on its wall (no parent wall in the
 * room) has no known swing at all, so it keeps the conservative box this
 * module used for every door before: the door's width along the wall and the
 * same again both into the room and out of it (`DOOR_SWING_MIN_M` at least).
 *
 * A doorless OPENING gets that same box. Nothing swings through it, but it is
 * a passage people walk through — a sweep along a part-open wall otherwise
 * crossed the gap, and one corpus basket stood in it.
 */

const { calculateDoorZone } = require('../../collision/doorCollision');
const { doPolygonsOverlap } = require('../../collision/geometry');
const { footprintFor } = require('../../geometry/objectFootprint');
const { DOOR_SWING_MIN_M } = require('./constants');

/** Both-sides box for a door with no known swing (or a doorless opening),
 *  as a plan polygon. */
function unseatedDoorBox(door) {
  const ry = door.rotation?.y || 0;
  const reach = Math.max(door.dimensions?.x || 0, DOOR_SWING_MIN_M);
  const along = { x: Math.cos(ry), z: -Math.sin(ry) };
  const n = { x: -along.z, z: along.x };
  const at = (a, b) => ({
    x: door.center.x + along.x * a + n.x * b,
    z: door.center.z + along.z * a + n.z * b,
  });
  return [
    at(-reach / 2, -reach),
    at(reach / 2, -reach),
    at(reach / 2, reach),
    at(-reach / 2, reach),
  ];
}

/**
 * Every door's swing zone in the room, and every doorless opening's
 * passage, as convex plan polygons.
 *
 * @param {object} room  a hydrated room (`room.doors` / `room.openings` with
 *   real geometry, `room.walls` to seat the doors on)
 * @returns {{x: number, z: number}[][]}
 */
function doorSwingZones(room) {
  const walls = room?.walls || [];
  const doors = (room?.doors || [])
    .filter((d) => d?.center)
    .flatMap((d) => calculateDoorZone(d, walls) || [unseatedDoorBox(d)]);
  const openings = (room?.openings || [])
    .filter((o) => o?.center)
    .map(unseatedDoorBox);
  return [...doors, ...openings];
}

/** Does this plan footprint stay out of every zone? */
const footprintClearsDoorSwings = (footprint, zones) =>
  !!footprint && zones.every((z) => !doPolygonsOverlap(footprint, z));

/**
 * Is a prop of half-size `half`, centred on `point`, clear of every zone?
 * Its footprint is the square it would claim, turned by `point.rotationY`.
 */
const clearsDoorSwings = (point, zones, half) =>
  footprintClearsDoorSwings(
    footprintFor(point, 2 * half, 2 * half, point.rotationY || 0),
    zones
  );

module.exports = {
  doorSwingZones,
  clearsDoorSwings,
  footprintClearsDoorSwings,
};
